import { AfterViewInit, Component, HostListener, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { NgIf } from '@angular/common';
import { StorageService } from '../../../services/storage.service';
import { GrayscaleService } from '../../../services/grayscale.service';
import { BhashiniService } from '../../../services/bhashini.service';

const FONT_SCALE_STORAGE_KEY = 'napsFontScale';
const DEFAULT_FONT_SCALE = 100;
const MIN_FONT_SCALE = 87.5;
const MAX_FONT_SCALE = 125;
const FONT_SCALE_STEP = 12.5;

@Component({
  selector: 'app-accessibility-bar',
  standalone: true,
  imports: [RouterLink, NgIf],
  templateUrl: './accessibility-bar.component.html',
  styleUrl: './accessibility-bar.component.scss',
})
export class AccessibilityBarComponent implements AfterViewInit, OnDestroy {
  private currentScale = DEFAULT_FONT_SCALE;

  isHomePage = false;
  private routerSub?: Subscription;

  constructor(
    private storageService: StorageService,
    private grayscaleService: GrayscaleService,
    private bhashiniService: BhashiniService,
    private router: Router,
  ) {
    const savedScale = Number(this.storageService.getData(FONT_SCALE_STORAGE_KEY));
    const initialScale = savedScale >= MIN_FONT_SCALE && savedScale <= MAX_FONT_SCALE ? savedScale : DEFAULT_FONT_SCALE;
    this.applyFontScale(initialScale);

    this.isHomePage = this.checkIsHomePage(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.isHomePage = this.checkIsHomePage(event.urlAfterRedirects);
      });
  }

  private checkIsHomePage(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || path === '';
  }

  ngAfterViewInit(): void {
    this.bhashiniService.loadScript();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  // Esc clears the visible keyboard-focus highlight from whichever control here has it.
  @HostListener('keydown.escape')
  onEscape(): void {
    (document.activeElement as HTMLElement)?.blur();
  }

  get isGrayscale(): boolean {
    return this.grayscaleService.isEnabled();
  }

  toggleGrayscale(): void {
    this.grayscaleService.toggle();
  }

  increaseFontSize(): void {
    this.applyFontScale(Math.min(MAX_FONT_SCALE, this.currentScale + FONT_SCALE_STEP));
  }

  decreaseFontSize(): void {
    this.applyFontScale(Math.max(MIN_FONT_SCALE, this.currentScale - FONT_SCALE_STEP));
  }

  resetFontSize(): void {
    this.applyFontScale(DEFAULT_FONT_SCALE);
  }

  private applyFontScale(scale: number): void {
    this.currentScale = scale;
    document.documentElement.style.fontSize = `${scale}%`;
    this.storageService.setData(FONT_SCALE_STORAGE_KEY, String(scale));
  }
}
